import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
  FormHelperText,
  Tooltip
} from '@mui/material';
import {
  Close,
  AttachFile,
  Delete,
  CheckCircle,
  CloudUpload,
  InsertDriveFile,
  CalendarToday,
  Visibility,
  Download
} from '@mui/icons-material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ClaimShipment, UploadFileAPI } from '../../apis/Agency';
import { successToast } from '../../components/Toasts';
import { useSelector } from 'react-redux';
import { base_logo_url } from '../../apis/constants';

const CreateClaimModal = ({
  open,
  onClose,
  shipment,
  onSubmit,
  initialData,
  isEditMode
}) => {
  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [errors, setErrors] = useState({});
  const [documents, setDocuments] = useState([]); // Local File objects
  const [uploadedDocuments, setUploadedDocuments] = useState([]); // Uploaded document data from API
  const [dragOver, setDragOver] = useState(false);
  const AgencyID = useSelector(state => state.userDetails.agencyLoginID);

  // Base URL for document viewing - will be configured later
  const [baseDocumentUrl, setBaseDocumentUrl] = useState(base_logo_url);

  // Initialize form data
  const [formData, setFormData] = useState({
    id: 0,
    agencyId: AgencyID,
    shipmentId: 0,
    claimStatus: 'Submitted',
    claimType: 'Damage',
    claimNumber: '',
    filedDate: new Date(),
    affectedClaimWeight: 0,
    affectedClaimQuantity: 0,
    affectedPONumber: '',
    affectedCommodities: '',
    claimAmount: 0,
    salvageReturned: '',
    concealedDamageLoss: '',
    additionalComments: '',
    claimAmountReceived: 0,
    carrierCheckNumber: '',
    paymentReceivingDate: new Date(),
    // Additional fields for easier mapping
    carrierClaimId: '',
    qtyDamaged: 0,
    damageDescription: '',
    claimedAmount: 0
  });

  const claimTypes = ['Total Loss', 'Damage', 'Shortage', 'Concealed Damage'];
  const claimStatuses = ['Submitted', 'Rejected', 'Paid'];

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, shipment, initialData, isEditMode]);

  const resetForm = () => {
    if (isEditMode && initialData) {
      // UPDATE MODE
      const apiToFormData = apiData => ({
        id: apiData.id || 0,
        agencyId: apiData.agencyId || 0,
        shipmentId: apiData.shipmentId || shipment?.id || 0,
        claimStatus: apiData.claimStatus || 'Submitted',
        claimType: apiData.claimType || 'Damage',
        claimNumber: apiData.claimNumber || '',
        filedDate: apiData.filedDate ? new Date(apiData.filedDate) : new Date(),
        affectedClaimWeight: apiData.affectedClaimWeight || 0,
        affectedClaimQuantity: apiData.affectedClaimQuantity || 0,
        affectedPONumber:
          apiData.affectedPONumber || shipment?.purchaseOrderNumber || '',
        affectedCommodities: apiData.affectedCommodities || '',
        salvageReturned: apiData.salvageReturned || '',
        concealedDamageLoss: apiData.concealedDamageLoss || '',
        additionalComments: apiData.additionalComments || '',
        claimAmountReceived: apiData.claimAmountReceived || 0,
        carrierCheckNumber: apiData.carrierCheckNumber || '',
        paymentReceivingDate: apiData.paymentReceivingDate
          ? new Date(apiData.paymentReceivingDate)
          : new Date().toISOString(),
        carrierClaimId: apiData.claimNumber || '',
        qtyDamaged: apiData.affectedClaimQuantity || 0,
        damageDescription: apiData.additionalComments || '',
        claimedAmount: apiData.claimAmount || 0
      });

      setFormData(apiToFormData(initialData));
      // Set uploaded documents from initial data
      setUploadedDocuments(initialData.documents || []);
      setDocuments([]); // Clear local files
    } else {
      // CREATE MODE
      const defaultData = {
        id: 0,
        agencyId: AgencyID,
        shipmentId: shipment?.id || 0,
        claimStatus: 'Submitted',
        claimType: 'Damage',
        claimNumber: '',
        filedDate: new Date(),
        affectedClaimWeight: 0,
        affectedClaimQuantity: 0,
        affectedPONumber: shipment?.purchaseOrderNumber || '',
        affectedCommodities: '',
        claimAmount: 0,
        salvageReturned: '',
        concealedDamageLoss: '',
        additionalComments: '',
        claimAmountReceived: 0,
        carrierCheckNumber: '',
        paymentReceivingDate: new Date(),
        carrierClaimId: '',
        qtyDamaged: 0,
        damageDescription: '',
        claimedAmount: 0
      };
      setFormData(defaultData);
      setDocuments([]);
      setUploadedDocuments([]);
    }
    setErrors({});
    setDragOver(false);
  };

  // Upload files to server
  const uploadFiles = async files => {
    setUploadingFiles(true);
    const uploaded = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        // Upload to "claims" destination
        const response = await UploadFileAPI('CLAIM_DOCUMENTS', formData);

        if (response?.data?.data) {
          uploaded.push({
            name: file.name,
            fileType: file.type || file.name.split('.').pop(),
            filePath: response.data.data, // URL from API response
            claimId: formData.id || 0
          });
        }
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      setUploadingFiles(false);
    }

    return uploaded;
  };

  const handleFileUpload = useCallback(
    async files => {
      const fileList = Array.from(files);
      // Remove file type validation to accept all files
      const validFiles = fileList; // Accept all files

      // Add to local documents list
      const newDocuments = [...documents, ...validFiles];
      setDocuments(newDocuments);

      // Upload files to server
      try {
        const uploaded = await uploadFiles(validFiles);
        setUploadedDocuments(prev => [...prev, ...uploaded]);
      } catch (error) {
        // Remove the files if upload fails
        setDocuments(prev => prev.filter(doc => !validFiles.includes(doc)));
        setErrors(prev => ({
          ...prev,
          documents: 'Failed to upload some files. Please try again.'
        }));
      }
    },
    [documents]
  );

  const handleFileSelect = event => {
    handleFileUpload(event.target.files);
  };

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileUpload(files);
      }
    },
    [handleFileUpload]
  );

  const handleRemoveDocument = (index, isUploaded = false) => {
    if (isUploaded) {
      // Remove from uploaded documents
      setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
    } else {
      // Remove from local documents (not yet uploaded)
      setDocuments(prev => prev.filter((_, i) => i !== index));
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = fileName => {
    const extension = fileName.split('.').pop().toLowerCase();
    if (['pdf'].includes(extension)) return '📄';
    if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) return '🖼️';
    if (['doc', 'docx'].includes(extension)) return '📝';
    return '📎';
  };

  // Function to construct full document URL
  const getDocumentUrl = filePath => {
    if (!filePath) return '';

    // Check if the filePath is already a full URL
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      return filePath;
    }

    // If baseDocumentUrl is set, prepend it to the filePath
    if (baseDocumentUrl) {
      // Remove any trailing slash from base URL and leading slash from filePath
      const cleanBaseUrl = baseDocumentUrl.replace(/\/$/, '');
      const cleanFilePath = filePath.replace(/^\//, '');
      return `${cleanBaseUrl}/${cleanFilePath}`;
    }

    // If no baseDocumentUrl is configured yet, return just the filePath
    return filePath;
  };

  // Function to handle viewing a document
  const handleViewDocument = (filePath, fileName) => {
    const fullUrl = getDocumentUrl(filePath);
    if (fullUrl) {
      window.open(fullUrl, '_blank', 'noopener,noreferrer');
    } else {
      console.error('Unable to construct document URL');
      // You could show a toast/alert here
    }
  };

  // Function to handle downloading a document
  const handleDownloadDocument = (filePath, fileName) => {
    const fullUrl = getDocumentUrl(filePath);
    if (fullUrl) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('Unable to construct document URL');
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    // 1. Carrier Claim ID (first required field)
    if (!formData.carrierClaimId?.trim() && !formData.claimNumber?.trim()) {
      newErrors.carrierClaimId =
        'Carrier Claim ID or Internal Claim Number is required';
    }
    // 2. Claim Amount
    else if ((formData.claimedAmount || formData.claimAmount) <= 0) {
      newErrors.claimedAmount = 'Claim amount must be greater than 0';
    }
    // 3. Quantity for Damage/Shortage types
    else if (
      (formData.claimType === 'Damage' || formData.claimType === 'Shortage') &&
      (!formData.qtyDamaged || formData.qtyDamaged <= 0)
    ) {
      newErrors.qtyDamaged =
        formData.claimType === 'Damage'
          ? 'Quantity damaged is required'
          : 'Shortage quantity is required';
    }
    // 4. Damage Description for Damage type
    else if (
      formData.claimType === 'Damage' &&
      !formData.damageDescription?.trim()
    ) {
      newErrors.damageDescription = 'Damage description is required';
    }
    // 5. Paid status validations (in order they appear)
    else if (formData.claimStatus === 'Paid') {
      if (!formData.paymentReceivingDate) {
        newErrors.paymentReceivingDate =
          'Payment receiving date is required for Paid claims';
      } else if ((formData.claimAmountReceived || 0) <= 0) {
        newErrors.claimAmountReceived =
          'Amount received must be greater than 0 for Paid claims';
      }
      //  else if (!formData.carrierCheckNumber?.trim()) {
      //   newErrors.carrierCheckNumber =
      //     'Carrier check number is required for Paid claims';
      // }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Prepare API payload
  const prepareApiPayload = () => {
    // Combine uploaded documents and any pending local uploads
    const allDocuments = [...uploadedDocuments];

    return {
      id: formData.id || 0,
      agencyId: AgencyID,
      shipmentId: formData.shipmentId || shipment?.id || 0,
      claimStatus: formData.claimStatus,
      claimType: formData.claimType,
      claimNumber: formData.carrierClaimId || formData.claimNumber,
      filedDate: formData.filedDate
        ? formData.filedDate.toISOString()
        : new Date().toISOString(),
      affectedClaimWeight: formData.affectedClaimWeight || 0,
      affectedClaimQuantity:
        formData.qtyDamaged || formData.affectedClaimQuantity || 0,
      affectedPONumber: formData.affectedPONumber || '',
      affectedCommodities: formData.affectedCommodities || '',
      claimAmount: formData.claimedAmount || formData.claimAmount || 0,
      salvageReturned: formData.salvageReturned || '',
      concealedDamageLoss: formData.concealedDamageLoss || '',
      additionalComments:
        formData.damageDescription || formData.additionalComments || '',
      claimAmountReceived: formData.claimAmountReceived || 0,
      carrierCheckNumber: formData.carrierCheckNumber || '',
      paymentReceivingDate: formData.paymentReceivingDate
        ? formData.paymentReceivingDate.toISOString()
        : new Date().toISOString(),
      documents: allDocuments
    };
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Upload any remaining local files
      // if (documents.length > 0) {
      //   const newlyUploaded = await uploadFiles(documents);
      //   setUploadedDocuments(prev => [...prev, ...newlyUploaded]);
      //   setDocuments([]);
      // }

      // Prepare the exact API payload structure
      const apiPayload = prepareApiPayload();

      // Call onSubmit with the properly formatted payload
      ClaimShipment(apiPayload)
        .then(res => {
          setDocuments([]);
          setUploadedDocuments([]);
          res.status === 200 &&
            successToast(
              isEditMode
                ? 'Claim updated successfully'
                : 'Claim created successfully'
            );
          onSubmit();
          onClose('success');
        })
        .catch(err => {
          console.log(err);
        });
      // await onSubmit(apiPayload);
      // onClose();
    } catch (err) {
      setDocuments([]);
      setUploadedDocuments([]);
      setErrors(prev => ({
        ...prev,
        _general: err instanceof Error ? err.message : 'Failed to submit claim'
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const showPaymentFields = formData.claimStatus === 'Paid';
  const dialogTitle = isEditMode ? 'Edit Claim' : 'Create Claim for Shipment';
  const submitButtonText = isEditMode ? 'Update Claim' : 'Submit Claim';

  const totalFiles = documents.length + uploadedDocuments.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      fullScreen={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1000px',
          height: '95vh',
          maxHeight: '900px',
          margin: 2
        }
      }}
    >
      <DialogTitle sx={{ p: 2, pb: 1 }}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' component='div'>
            {dialogTitle}
          </Typography>
          <IconButton
            onClick={onClose}
            size='small'
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary'
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <Typography variant='subtitle2' color='textSecondary'>
          {shipment &&
            `BOL: ${shipment.bolNumber} | PRO: ${shipment.proNumber} | Carrier: ${shipment.carrierName}`}
          {isEditMode && initialData && ` | Claim ID: ${initialData.id}`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 2 }}>
        {errors._general && (
          <Alert
            severity='error'
            sx={{
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: '0.875rem'
              }
            }}
            onClose={() =>
              setErrors(prev => ({ ...prev, _general: undefined }))
            }
          >
            {errors._general}
          </Alert>
        )}

        {/* Shipment Information */}
        {shipment && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 3,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}
          >
            <Typography
              variant='subtitle2'
              gutterBottom
              fontWeight='bold'
              sx={{ fontSize: '0.875rem' }}
            >
              SHIPMENT DETAILS
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size='small'
                  label='Origin'
                  value={`${shipment?.originCity || ''}, ${
                    shipment?.originState || ''
                  }, ${shipment?.originZipCode || ''}`}
                  InputProps={{
                    readOnly: true,
                    sx: { fontSize: '0.875rem' }
                  }}
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size='small'
                  label='Destination'
                  value={`${shipment?.destinationCity || ''}, ${
                    shipment?.destinationState || ''
                  }, ${shipment?.destinationZipCode || ''}`}
                  InputProps={{
                    readOnly: true,
                    sx: { fontSize: '0.875rem' }
                  }}
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size='small'
                  label='Shipment Value'
                  value={`$${
                    shipment?.shipmentCharges?.toLocaleString() || '0.00'
                  }`}
                  InputProps={{
                    readOnly: true,
                    sx: { fontSize: '0.875rem' }
                  }}
                  InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                />
              </Grid>
              {shipment?.totalPieces > 0 && (
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    size='small'
                    label='Total Pieces'
                    value={shipment.totalPieces}
                    InputProps={{
                      readOnly: true,
                      sx: { fontSize: '0.875rem' }
                    }}
                    InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {/* Claim Form */}
        <Grid container spacing={2}>
          {/* Row 1: Claim Status & Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size='small' required>
              <InputLabel sx={{ fontSize: '0.875rem' }}>
                Claim Status
              </InputLabel>
              <Select
                value={formData.claimStatus}
                label='Claim Status'
                onChange={e => handleInputChange('claimStatus', e.target.value)}
                sx={{ fontSize: '0.875rem' }}
              >
                {claimStatuses.map(status => (
                  <MenuItem
                    key={status}
                    value={status}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth size='small' required>
              <InputLabel sx={{ fontSize: '0.875rem' }}>Claim Type</InputLabel>
              <Select
                disabled={isEditMode}
                value={formData.claimType}
                label='Claim Type'
                onChange={e => handleInputChange('claimType', e.target.value)}
                sx={{ fontSize: '0.875rem' }}
              >
                {claimTypes.map(type => (
                  <MenuItem
                    key={type}
                    value={type}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Row 2: Claim Numbers */}
          <Grid item xs={12} md={6}>
            <TextField
              disabled={isEditMode}
              fullWidth
              size='small'
              required
              label='Carrier Claim ID'
              value={formData.carrierClaimId}
              onChange={e =>
                handleInputChange('carrierClaimId', e.target.value)
              }
              placeholder='Enter carrier-provided claim ID'
              error={!!errors.carrierClaimId}
              helperText={errors.carrierClaimId}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ style: { fontSize: '0.875rem' } }}
              FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>

          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size='small'
              label='Internal Claim Number'
              value={formData.claimNumber}
              onChange={e => handleInputChange('claimNumber', e.target.value)}
              placeholder='Optional internal reference'
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ style: { fontSize: '0.875rem' } }}
            />
          </Grid> */}

          {/* Row 4: Quantities */}
          {(formData.claimType === 'Damage' ||
            formData.claimType === 'Shortage') && (
            <Grid item xs={12} md={6}>
              <TextField
                // disabled={isEditMode}
                fullWidth
                size='small'
                required
                type='number'
                label={
                  formData.claimType === 'Damage'
                    ? 'Quantity Damaged'
                    : 'Shortage Quantity'
                }
                value={formData.qtyDamaged || ''}
                onChange={e =>
                  handleInputChange('qtyDamaged', parseInt(e.target.value) || 0)
                }
                error={!!errors.qtyDamaged}
                helperText={errors.qtyDamaged}
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                inputProps={{
                  min: 1,
                  style: { fontSize: '0.875rem' }
                }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>
          )}

          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size='small'
              label='Affected Commodities'
              value={formData.affectedCommodities}
              onChange={e =>
                handleInputChange('affectedCommodities', e.target.value)
              }
              placeholder='Describe affected items'
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ style: { fontSize: '0.875rem' } }}
            />
          </Grid> */}

          {/* Row 5: Purchase Order */}
          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size='small'
              label='Affected PO Number'
              value={formData.affectedPONumber}
              onChange={e =>
                handleInputChange('affectedPONumber', e.target.value)
              }
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ style: { fontSize: '0.875rem' } }}
            />
          </Grid> */}

          {/* Row 6: Amounts */}
          <Grid item xs={12} md={6}>
            <TextField
              // disabled={isEditMode}
              fullWidth
              size='small'
              required
              type='number'
              label='Claim Amount ($)'
              value={formData.claimedAmount || formData.claimAmount || ''}
              onChange={e =>
                handleInputChange(
                  'claimedAmount',
                  parseFloat(e.target.value) || 0
                )
              }
              error={!!errors.claimedAmount}
              helperText={errors.claimedAmount}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>$</InputAdornment>
                ),
                style: { fontSize: '0.875rem' }
              }}
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{
                min: 0,
                step: 0.01
              }}
              FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
            />
          </Grid>

          {showPaymentFields && (
            <Grid item xs={12} md={6}>
              <TextField
                disabled={isEditMode && formData.claimStatus !== 'Paid'}
                fullWidth
                size='small'
                required
                type='number'
                label='Amount Received ($)'
                value={formData.claimAmountReceived || ''}
                onChange={e =>
                  handleInputChange(
                    'claimAmountReceived',
                    parseFloat(e.target.value) || 0
                  )
                }
                error={!!errors.claimAmountReceived}
                helperText={errors.claimAmountReceived}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>$</InputAdornment>
                  ),
                  style: { fontSize: '0.875rem' }
                }}
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                inputProps={{
                  min: 0,
                  step: 0.01,
                  style: { fontSize: '0.875rem' }
                }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>
          )}

          {/* Row 7: Payment Details (if Paid) */}
          {/* {showPaymentFields && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled={isEditMode}
                size='small'
                required
                label='Carrier Check Number'
                value={formData.carrierCheckNumber}
                onChange={e =>
                  handleInputChange('carrierCheckNumber', e.target.value)
                }
                error={!!errors.carrierCheckNumber}
                helperText={errors.carrierCheckNumber}
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                inputProps={{ style: { fontSize: '0.875rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>
          )} */}

          {/* Row 8: Additional Information */}
          {/* <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size='small'
              label='Salvage Returned'
              value={formData.salvageReturned}
              onChange={e =>
                handleInputChange('salvageReturned', e.target.value)
              }
              placeholder='Describe any returned salvage'
              InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
              inputProps={{ style: { fontSize: '0.875rem' } }}
            />
          </Grid> */}

          {formData.claimType === 'Concealed Damage' && (
            <Grid item xs={12} md={6}>
              <TextField
                disabled={isEditMode}
                fullWidth
                size='small'
                label='Concealed Damage Details'
                value={formData.concealedDamageLoss}
                onChange={e =>
                  handleInputChange('concealedDamageLoss', e.target.value)
                }
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                inputProps={{ style: { fontSize: '0.875rem' } }}
              />
            </Grid>
          )}

          {/* Row 9: Description/Comments */}
          {formData.claimType === 'Damage' && (
            <Grid item xs={12}>
              <TextField
                disabled={isEditMode}
                fullWidth
                size='small'
                required={formData.claimType === 'Damage'}
                label={
                  formData.claimType === 'Damage'
                    ? 'Damage Description'
                    : 'Additional Comments'
                }
                value={
                  formData.damageDescription ||
                  formData.additionalComments ||
                  ''
                }
                onChange={e =>
                  handleInputChange(
                    formData.claimType === 'Damage'
                      ? 'damageDescription'
                      : 'additionalComments',
                    e.target.value
                  )
                }
                error={!!errors.damageDescription}
                helperText={errors.damageDescription}
                multiline
                rows={2}
                placeholder={
                  formData.claimType === 'Damage'
                    ? 'Describe the damage in detail...'
                    : 'Enter any additional comments...'
                }
                InputLabelProps={{ sx: { fontSize: '0.875rem' } }}
                inputProps={{ style: { fontSize: '0.875rem' } }}
                FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
              />
            </Grid>
          )}

          {/* Row 3: Dates */}

          {showPaymentFields && (
            <Grid item xs={12} md={12}>
              <Box>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ fontSize: '0.75rem', mb: 0.5, display: 'block' }}
                >
                  Payment Receiving Date *
                </Typography>
                <DatePicker
                  disabled={isEditMode}
                  selected={formData.paymentReceivingDate}
                  onChange={date =>
                    handleInputChange('paymentReceivingDate', date)
                  }
                  dateFormat='MM/dd/yyyy'
                  customInput={
                    <TextField
                      fullWidth
                      size='small'
                      required
                      error={!!errors.paymentReceivingDate}
                      helperText={errors.paymentReceivingDate}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <CalendarToday fontSize='small' />
                          </InputAdornment>
                        ),
                        sx: { fontSize: '0.875rem' }
                      }}
                      FormHelperTextProps={{ sx: { fontSize: '0.75rem' } }}
                    />
                  }
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={12}>
            <Box>
              <Typography
                variant='caption'
                color='textSecondary'
                sx={{ fontSize: '0.75rem', mb: 0.5, display: 'block' }}
              >
                Claim Filed Date
              </Typography>
              <DatePicker
                disabled={isEditMode}
                selected={formData.filedDate}
                onChange={date => handleInputChange('filedDate', date)}
                dateFormat='MM/dd/yyyy'
                customInput={
                  <TextField
                    fullWidth
                    size='small'
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position='start'>
                          <CalendarToday fontSize='small' />
                        </InputAdornment>
                      ),
                      sx: { fontSize: '0.765rem' }
                    }}
                  />
                }
              />
            </Box>
          </Grid>
          {/* File Upload Section */}
          <Grid item xs={12}>
            <Box mt={2}>
              <Typography
                variant='subtitle2'
                gutterBottom
                fontWeight='bold'
                sx={{ fontSize: '0.875rem' }}
              >
                SUPPORTING DOCUMENTS
              </Typography>

              {errors.documents && (
                <Typography
                  color='error'
                  variant='caption'
                  sx={{ fontSize: '0.75rem', display: 'block', mb: 1 }}
                >
                  {errors.documents}
                </Typography>
              )}

              {/* Drag & Drop Area */}
              <Paper
                disabled={isEditMode}
                elevation={dragOver ? 8 : 1}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  p: 3,
                  mb: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  backgroundColor: dragOver
                    ? 'action.hover'
                    : 'background.paper',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': isEditMode
                    ? {}
                    : {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover'
                      }
                }}
                onClick={() =>
                  document.getElementById('claim-documents').click()
                }
              >
                <input
                  // Remove accept attribute to allow all file types
                  style={{ display: 'none' }}
                  id='claim-documents'
                  type='file'
                  multiple
                  onChange={handleFileSelect}
                  disabled={uploadingFiles || isEditMode}
                />
                {uploadingFiles ? (
                  <CircularProgress size={48} sx={{ mb: 1 }} />
                ) : (
                  <CloudUpload
                    sx={{
                      fontSize: 48,
                      color: dragOver ? 'primary.main' : 'text.secondary',
                      mb: 1
                    }}
                  />
                )}
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{ mb: 0.5 }}
                >
                  {uploadingFiles
                    ? 'Uploading files...'
                    : 'Drag & drop files here or click to browse'}
                </Typography>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ fontSize: '0.75rem' }}
                >
                  Supports all file types
                </Typography>
              </Paper>

              {/* Upload Button */}
              <Box display='flex' justifyContent='center' mb={2}>
                <Button
                  variant='outlined'
                  component='span'
                  startIcon={<AttachFile />}
                  size='small'
                  onClick={() =>
                    document.getElementById('claim-documents').click()
                  }
                  sx={{ fontSize: '0.8125rem' }}
                  disabled={uploadingFiles || isEditMode}
                >
                  {uploadingFiles ? 'Uploading...' : 'Browse Files'}
                </Button>
              </Box>

              {/* Document Lists */}
              {(documents.length > 0 || uploadedDocuments.length > 0) && (
                <Paper
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: '200px',
                    overflow: 'auto'
                  }}
                >
                  {/* Uploaded files - Now with View/Download buttons */}
                  {uploadedDocuments.map((doc, index) => (
                    <Box
                      key={`uploaded-${index}`}
                      display='flex'
                      alignItems='center'
                      justifyContent='space-between'
                      p={1.5}
                      sx={{
                        borderBottom:
                          index < uploadedDocuments.length - 1
                            ? '1px solid'
                            : 'none',
                        borderColor: 'divider',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Box display='flex' alignItems='center' flex={1}>
                        <InsertDriveFile
                          fontSize='small'
                          sx={{
                            mr: 1.5,
                            color: 'success.main'
                          }}
                        />
                        <Box flex={1} minWidth={0}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontSize: '0.8125rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {doc.name} ✓
                          </Typography>
                          <Typography
                            variant='caption'
                            color='textSecondary'
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Uploaded
                          </Typography>
                        </Box>
                      </Box>
                      <Box display='flex' alignItems='center' gap={0.5}>
                        <Chip
                          label={getFileIcon(doc.name)}
                          size='small'
                          sx={{
                            mr: 0.5,
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                        {/* View Button */}
                        <Tooltip title='View document' arrow>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleViewDocument(doc.filePath, doc.name)
                            }
                            sx={{
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'primary.lighter'
                              }
                            }}
                          >
                            <Visibility fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        {/* Download Button */}
                        <Tooltip title='Download document' arrow>
                          <IconButton
                            size='small'
                            onClick={() =>
                              handleDownloadDocument(doc.filePath, doc.name)
                            }
                            sx={{
                              color: 'secondary.main',
                              '&:hover': {
                                backgroundColor: 'secondary.lighter'
                              }
                            }}
                          >
                            <Download fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        {/* Delete Button */}
                        <Tooltip title='Remove document' arrow>
                          <IconButton
                            disabled={isEditMode}
                            size='small'
                            onClick={() => handleRemoveDocument(index, true)}
                            sx={{
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.lighter'
                              }
                            }}
                          >
                            <Delete fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              )}

              {/* File Count Summary */}
              {/* {totalFiles > 0 && (
                <Box display='flex' justifyContent='space-between' mt={1}>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {totalFiles} file(s) attached
                    {documents.length > 0 &&
                      ` (${documents.length} pending upload)`}
                  </Typography>
                </Box>
              )} */}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading || uploadingFiles}
          size='small'
          sx={{ fontSize: '0.8125rem' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant='contained'
          color='primary'
          disabled={loading || uploadingFiles}
          startIcon={
            loading || uploadingFiles ? (
              <CircularProgress size={16} />
            ) : (
              <CheckCircle />
            )
          }
          size='small'
          sx={{ fontSize: '0.8125rem' }}
        >
          {uploadingFiles
            ? 'Uploading...'
            : loading
              ? 'Processing...'
              : submitButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateClaimModal;
